/**
 * Mixer levels — the faders on the band's channels and the master volume.
 *
 * A LEVEL is the fader position (1 = unity, the calibrated balance every
 * voice ships at); a GAIN is what the bus multiplies by. The law between
 * them is SQUARED: half a fader is −12 dB and a channel fader's top (1.5)
 * is +3.5 dB of boost. A linear amplitude fader spends its whole bottom
 * half on the last 6 dB and reads as doing nothing until the very end;
 * squaring puts the audible range along the travel. The MASTER fader only
 * attenuates (0..1) and sits AFTER the bus compressor and the limiter, so
 * turning the band down never changes how hard either works and never
 * opens a way past the safety.
 */
export const MIX_CHANNELS = ['melody', 'comp', 'bass', 'drums'] as const;
export type MixChannel = (typeof MIX_CHANNELS)[number];
/** The band's channels, the metronome's click, and the master. Solo acts on the band alone. */
export type MixFader = MixChannel | 'click' | 'master';
export type ChannelLevels = Record<MixFader, number>;

export const CHANNEL_LEVEL_MAX = 1.5;
export const MASTER_LEVEL_MAX = 1;

export const UNITY_LEVELS: ChannelLevels = {
  melody: 1,
  comp: 1,
  bass: 1,
  drums: 1,
  click: 1,
  master: 1,
};

export function levelMax(fader: MixFader): number {
  return fader === 'master' ? MASTER_LEVEL_MAX : CHANNEL_LEVEL_MAX;
}

/** Clamp one fader position into its range; junk reads as unity. */
export function normalizeLevel(fader: MixFader, level: unknown): number {
  if (typeof level !== 'number' || !Number.isFinite(level)) return UNITY_LEVELS[fader];
  return Math.min(levelMax(fader), Math.max(0, level));
}

/** A whole level set from untrusted input (a persisted blob, a command). */
export function normalizeLevels(input: unknown): ChannelLevels {
  const src = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const out = { ...UNITY_LEVELS };
  for (const fader of Object.keys(UNITY_LEVELS) as MixFader[]) {
    out[fader] = normalizeLevel(fader, src[fader]);
  }
  return out;
}

/** Fader position → bus gain. */
export function levelGain(level: number): number {
  const l = Number.isFinite(level) ? Math.max(0, level) : 1;
  return l * l;
}

/**
 * The levels the player hears under a SOLO: the soloed channel at its own
 * fader, every other channel silent, the master untouched. The faders
 * themselves never move, so clearing the solo gives the mix back exactly.
 */
export function soloedLevels(levels: ChannelLevels, solo: MixChannel | null): ChannelLevels {
  if (!solo) return levels;
  const out = { ...levels };
  for (const channel of MIX_CHANNELS) if (channel !== solo) out[channel] = 0;
  return out;
}
